<template>
    <aside class="w-80 shrink-0 flex-col border-r border-default bg-default">
        <div class="flex min-h-[104px] items-center justify-between border-b border-default px-4 py-3">
            <LogoComponent />
        </div>

        <nav class="flex-1 overflow-y-auto px-5 pt-0 pb-2">
            <ul class="m-0 list-none p-0">
                <li v-for="item of items" :key="item.name">
                    <RouterLink
                        :to="{ name: item.name }"
                        class="nav-link"
                        :class="{ 'nav-link--active': isActive(item.name) }"
                        @click="emit('navigate')"
                    >
                        {{ item.label }}
                    </RouterLink>
                </li>
            </ul>
        </nav>

        <div class="mt-auto flex w-full items-center gap-8 px-4 py-4">
            <a href="https://www.imagine.si/" class="max-h-20 w-full" target="_blank">
                <img alt="Imagine" src="@/assets/images/imagine-logo.png">
            </a>
            <a href="https://it-team.global/" class="max-h-20 w-full" target="_blank">
                <img alt="IT TEAM d.o.o." src="@/assets/images/it-team-logo.png">
            </a>
        </div>
    </aside>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { RouterLink, useRoute } from 'vue-router'

    import LogoComponent from '@/components/LogoComponent.vue'

    /**
     * Plain router links rather than UNavigationMenu: the portal's sidebar has
     * no item icons and uses a right-rounded active row with a 4px accent bar
     * down its left edge, none of which UNavigationMenu can express without
     * overriding most of its slots anyway.
     */

    const emit = defineEmits<(e: 'navigate') => void>()

    const { t } = useI18n()
    const route = useRoute()

    const items = computed(() => [
        { name: 'users', label: t('navigation.users') },
        { name: 'partners', label: t('navigation.partners') },
        { name: 'depository', label: t('navigation.depositories') },
        { name: 'bulletin-board', label: t('navigation.bulletin_board') },
        { name: 'faq', label: t('navigation.faq') },
        { name: 'allowed-pages', label: t('navigation.allowed_pages') }
    ])

    // Child routes keep their parent highlighted, e.g. a depository's contents.
    const isActive = (name: string) => route.matched.some(record => record.name === name)
</script>

<style scoped>
.nav-link {
    position: relative;
    display: flex;
    align-items: center;
    margin: 2px 0;
    padding: 0.55rem 0.75rem 0.55rem 1.375rem;
    width: 100%;
    border-top-right-radius: var(--ui-radius);
    border-bottom-right-radius: var(--ui-radius);
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--ui-text-highlighted);
    transition: color 150ms, background-color 150ms;
}

.nav-link::first-letter {
    font-size: 1.4rem;
}

.nav-link:hover {
    color: var(--ui-primary);
    background-color: var(--ui-bg-elevated);
}

.nav-link--active {
    color: var(--ui-primary);
    background-color: var(--color-primary-soft);
}

.nav-link--active::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: var(--ui-primary);
}
</style>
